import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../cn";

export interface SettingsCardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** A small uppercase name for the group of rows below it. */
  title?: ReactNode;
  /** A sunk strip under the rows — a short note, not a second card. */
  footer?: ReactNode;
  children?: ReactNode;
}

/**
 * A card of label-and-control rows, for a settings screen.
 *
 * Not `Card` with `SettingsRow` children dropped in loose: the rule between
 * rows has to be drawn once, by the container, or every caller reinvents
 * "divide these" and one of them forgets the last row.
 */
export function SettingsCard({
  title,
  footer,
  children,
  className,
  ...props
}: SettingsCardProps) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-[var(--radius-card)] border border-rule/80 bg-surface",
        className,
      )}
      {...props}
    >
      {title ? (
        <div className="border-b border-rule px-5 py-3">
          <span className="font-mono text-micro tracking-[0.08em] text-ink-3 uppercase">
            {title}
          </span>
        </div>
      ) : null}
      <div className="divide-y divide-rule">{children}</div>
      {footer ? <div className="bg-sunk px-5 py-3 text-meta text-ink-3">{footer}</div> : null}
    </div>
  );
}

export interface SettingsRowProps extends HTMLAttributes<HTMLDivElement> {
  label: ReactNode;
  hint?: ReactNode;
  /** The control or value on the right. */
  children?: ReactNode;
}

/**
 * One row of a `SettingsCard`: label and hint in a fixed left column, the
 * control on the right. Stacks to one column under ~640px, where a fixed
 * column would otherwise crowd the control against the edge of the page.
 */
export function SettingsRow({ label, hint, children, className, ...props }: SettingsRowProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start sm:gap-6",
        className,
      )}
      {...props}
    >
      <div className="flex flex-col gap-0.5 sm:w-64 sm:shrink-0">
        <span className="font-sans text-ui text-ink">{label}</span>
        {hint ? <p className="text-meta text-ink-3">{hint}</p> : null}
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
